import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { esAdmin } from "@/lib/roles";

import AdminLogin from "@/pages/admin/Login";
import Dashboard from "@/pages/admin/Dashboard";
import NewReservation from "@/pages/admin/NewReservation";
import ReservationDetail from "@/pages/admin/ReservationDetail";
import Products from "@/pages/admin/Products";
import Fleet from "@/pages/admin/Fleet";
import Seasons from "@/pages/admin/Seasons";
import Calendar from "@/pages/admin/Calendar";
import BookingSettings from "@/pages/admin/BookingSettings";
import CompanySettings from "@/pages/admin/CompanySettings";
import { applyBrand, loadBrand } from "@/lib/brand";
import { necesitaSegundoFactor } from "@/lib/segundoFactor";
import BookingHome from "@/pages/public/BookingHome";
import VehicleBooking from "@/pages/public/VehicleBooking";
import BookingResult from "@/pages/public/BookingResult";
import BookingCancelled from "@/pages/public/BookingCancelled";
import PublicTerms from "@/pages/public/PublicTerms";
import LegalPage from "@/pages/public/LegalPage";
import LegalSettings from "@/pages/admin/LegalSettings";
import PrivacySettings from "@/pages/admin/PrivacySettings";
import ContractSettings from "@/pages/admin/ContractSettings";
import Users from "@/pages/admin/Users";
import AssistantSettings from "@/pages/admin/AssistantSettings";
import Payments from "@/pages/admin/Payments";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import Finance from "@/pages/admin/Finance";
import ReturnChecklist from "@/pages/admin/ReturnChecklist";
import RegistrySettings from "@/pages/admin/RegistrySettings";
import Registry from "@/pages/admin/Registry";
import ClientWelcome from "@/pages/client/Welcome";
import ClientCheckin from "@/pages/client/Checkin";
import ClientCheckout from "@/pages/client/Checkout";
import ClientSelfPickup from "@/pages/client/SelfPickup";
import PaymentResult from "@/pages/client/PaymentResult";
import ClientReturnPrep from "@/pages/client/ReturnPrep";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#5C5C5C]">
        Cargando...
      </div>
    );
  if (!user) return <Navigate to="/admin/login" replace />;
  // La instalación exige el segundo factor y aún no lo tiene: solo «Usuarios», para activarlo (el backend da 403).
  if (necesitaSegundoFactor(user) && window.location.pathname !== "/admin/usuarios") {
    return <Navigate to="/admin/usuarios" replace />;
  }
  return children;
}

// Pantallas de ajustes: el operador vuelve al panel (el backend le respondería 403 igualmente).
function SoloAdmin({ children }) {
  const { user } = useAuth();
  return esAdmin(user) ? children : <Navigate to="/admin" replace />;
}

export default function App() {
  useEffect(() => {
    loadBrand().then(applyBrand);
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <Toaster position="top-center" richColors closeButton />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/reservar" replace />} />
            <Route path="/reservar" element={<BookingHome />} />
            <Route path="/embed/reservar" element={<BookingHome embedded />} />
            <Route path="/reservar/resultado" element={<BookingResult />} />
            <Route path="/condiciones" element={<PublicTerms />} />
            <Route path="/privacidad" element={<LegalPage doc="privacy" />} />
            <Route path="/aviso-legal" element={<LegalPage doc="notice" />} />
            <Route path="/reservar/cancelado" element={<BookingCancelled />} />
            <Route path="/reservar/:slug" element={<VehicleBooking />} />
            <Route path="/embed/reservar/:slug" element={<VehicleBooking embedded />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <Protected>
                  <Dashboard />
                </Protected>
              }
            />
            <Route
              path="/admin/reservations/new"
              element={
                <Protected>
                  <NewReservation />
                </Protected>
              }
            />
            <Route
              path="/admin/reservations/:id"
              element={
                <Protected>
                  <ReservationDetail />
                </Protected>
              }
            />
            <Route
              path="/admin/productos"
              element={
                <Protected>
                  <SoloAdmin>
                    <Products />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/flota"
              element={
                <Protected>
                  <SoloAdmin>
                    <Fleet />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/temporadas"
              element={
                <Protected>
                  <SoloAdmin>
                    <Seasons />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route path="/admin/recuperar" element={<ForgotPassword />} />
            <Route path="/admin/restablecer" element={<ResetPassword />} />
            <Route
              path="/admin/textos-legales"
              element={
                <Protected>
                  <SoloAdmin>
                    <LegalSettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/privacidad"
              element={
                <Protected>
                  <SoloAdmin>
                    <PrivacySettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/cobros"
              element={
                <Protected>
                  <SoloAdmin>
                    <Payments />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/asistente"
              element={
                <Protected>
                  <SoloAdmin>
                    <AssistantSettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/usuarios"
              element={
                <Protected>
                  <Users />
                </Protected>
              }
            />
            <Route
              path="/admin/contrato"
              element={
                <Protected>
                  <SoloAdmin>
                    <ContractSettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/empresa"
              element={
                <Protected>
                  <SoloAdmin>
                    <CompanySettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/ajustes-reserva"
              element={
                <Protected>
                  <SoloAdmin>
                    <BookingSettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/calendario"
              element={
                <Protected>
                  <Calendar />
                </Protected>
              }
            />
            <Route
              path="/admin/finanzas"
              element={
                <Protected>
                  <SoloAdmin>
                    <Finance />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/checklist"
              element={
                <Protected>
                  <SoloAdmin>
                    <ReturnChecklist />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route
              path="/admin/registro-viajeros"
              element={
                <Protected>
                  <Registry />
                </Protected>
              }
            />
            <Route
              path="/admin/registro-viajeros/ajustes"
              element={
                <Protected>
                  <SoloAdmin>
                    <RegistrySettings />
                  </SoloAdmin>
                </Protected>
              }
            />
            <Route path="/reserva/:code" element={<ClientWelcome />} />
            <Route path="/checkin/:code" element={<ClientCheckin />} />
            <Route path="/recogida/:code" element={<ClientSelfPickup />} />
            <Route path="/checkout/:code" element={<ClientCheckout />} />
            <Route path="/devolucion/:code" element={<ClientReturnPrep />} />
            <Route path="/pago/:result" element={<PaymentResult />} />
            <Route path="/pago-extras/:result" element={<PaymentResult kind="extras" />} />
            <Route path="/pago-alquiler/:result" element={<PaymentResult kind="rental" />} />
            <Route path="/pago-senal/:result" element={<PaymentResult kind="signal" />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
